import Button from "@/components/button/Button"
import { useAuth } from "@/components/context/AuthProvider"
import { useModalDarkContext } from "@/components/context/ModalDarkContext"
import WebsiteButton from "@/components/website-button/WebsiteButton"
import WebsiteTypography from "@/components/websiteTypography/WebsiteTypography"
import { useCreateContactRequestMutation, useSupportTopicsQuery } from "@/graphql"
import { useBreakpoint } from "@/hooks/useBreakpoint"
import WebsiteFormItem from "@/pages/website-landing/components/website-form-item/WebsiteFormItem"
import { globalColors } from "@/utilities/global-variables"
import { Col, Form, FormInstance, Row, message } from "antd"
import { FC } from "react"

import { trackEvent } from "@/hooks/useGoogleAnalytics"
import "../../../../../../styles/website-form-elements.less"

const { Text } = WebsiteTypography

const ContactForm: FC<{ isDashboard: boolean }> = ({ isDashboard }) => {
  const [form] = Form.useForm()
  const { user } = useAuth()
  const { setIsModalDarkOpen, setModalDarkTypes } = useModalDarkContext()
  const [createContactRequest, { loading }] = useCreateContactRequestMutation()

  const { data: supportData, loading: topicsLoading } = useSupportTopicsQuery()

  const optionsData = supportData?.supportTopics?.data

  const options =
    optionsData?.map(item => ({
      label: item.attributes?.title,
      value: item.id,
      description: item.attributes?.description,
    })) || []

  const onFinish = async (values: Partial<ContactRequestInput>) => {
    await createContactRequest({
      variables: {
        input: {
          ...values,
          isDashboard: isDashboard,
          type: isDashboard ? "support" : "contact",
        },
      },
      onError: error => {
        message.error(error.message)
      },
      onCompleted: () => {
        message.success("Request sent successfully")
        form.resetFields()
        // Track successful form submission
        trackEvent("Book a demo banner", "Book a demo click", "quick-contact-form")
        // setIsModalDarkOpen(false)
        setModalDarkTypes("success")
      },
    })
  }

  const { xxl, xl, lg, md, sm, xs } = useBreakpoint()

  const initialValues = {
    fullName: user?.userProfile?.data?.attributes?.fullName ?? "",
    company: user?.userProfile?.data?.attributes?.company?.data?.attributes?.name ?? "",
    email: user?.userProfile?.data?.attributes?.primaryEmail ?? "",
    phoneNumber: user?.userProfile?.data?.attributes?.primaryPhone ?? "",
  }

  return (
    <Form<Partial<ContactRequestInput>>
      onFinish={onFinish}
      autoComplete={"off"}
      form={form as FormInstance<ContactRequestInput>}
      initialValues={initialValues}
    >
      <Row gutter={xxl || xl || lg ? 24 : md || sm || xs ? [0, 24] : 24} style={{ marginBottom: 20, marginTop: 40 }}>
        <Col span={12} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <WebsiteFormItem type={"input"} name={"fullName"} placeholder={"Full Name *"} required={true} />
        </Col>
        <Col span={12} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <WebsiteFormItem type={"input"} name={"company"} placeholder={"Company Name *"} required={true} />
        </Col>
      </Row>
      <Row gutter={xxl || xl || lg ? 24 : md || sm || xs ? [0, 24] : 24} style={{ marginBottom: 20 }}>
        <Col span={12} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <WebsiteFormItem
            type={"input-email"}
            name={"email"}
            placeholder={"Email *"}
            required={true}
            // @ts-expect-error: type mismatch between FormInstance and inherited props
            form={form}
          />
        </Col>
        <Col span={12} xxl={12} xl={12} lg={12} md={24} sm={24} xs={24}>
          <WebsiteFormItem
            type={"tel-input"}
            name={"phoneNumber"}
            form={form as FormInstance & string}
            placeholder={"Phone *"}
          />
        </Col>
      </Row>
      <Row>
        {isDashboard && (
          <Col span={24} style={{ paddingBottom: 16 }}>
            <WebsiteFormItem
              type={"select"}
              name={"supportTopic"}
              placeholder={"Choose the Topic of Your Question"}
              options={options.map(opt => ({
                ...opt,
                label: (
                  <div>
                    <div>{opt.label}</div>
                    <div style={{ fontSize: 12, opacity: 0.6, paddingTop: 2 }}>{opt.description}</div>
                  </div>
                ),
              }))}
              loading={topicsLoading}
              required={true}
            />
          </Col>
        )}
        <Col span={24}>
          <WebsiteFormItem
            type={"textarea"}
            name={"message"}
            placeholder={"Write your message here..."}
            rows={5}
            minRows={5}
          />
        </Col>
      </Row>

      <Row style={{ marginTop: 16 }} justify={"center"}>
        <Text centered color={"website-secondary"} size={"xs"} style={{ paddingBottom: 16 }}>
          {
            "By clicking submit below, you consent to allow Easetech to store and process the personal information submitted above to provide you the content requested."
          }
        </Text>
      </Row>

      <Row justify={"center"} style={{ paddingTop: 16 }}>
        <Button size={"large"} htmlType={"submit"} loading={loading}>
          <Text size={"md"}>{"SUBMIT NOW"}</Text>
        </Button>
      </Row>
    </Form>
  )
}

export default ContactForm
